/**
 * Whiteboard Import Utilities
 * static/js/whiteboard/import.js
 * 
 * .excalidraw 檔案匯入功能
 */

import { SUPPORTED_IMPORT_EXTENSIONS, MAX_FILE_SIZE_BYTES, MESSAGES } from './config.js';
import { getExcalidrawAPI } from './state.js';
import { showToast } from './ui.js';

/**
 * 匯入 Excalidraw 檔案
 * @param {File} file 
 * @returns {Promise<Object|null>} - 解析後的場景資料
 */
export async function importExcalidrawFile(file) {
  // 驗證檔案類型
  if (!isValidFileType(file.name)) {
    showToast(MESSAGES.INVALID_FILE, 'error');
    return null;
  }

  // 驗證檔案大小
  if (file.size > MAX_FILE_SIZE_BYTES) {
    showToast(MESSAGES.FILE_TOO_LARGE, 'error');
    return null;
  }

  try {
    // 根據檔案類型選擇解析方式
    if (file.name.endsWith('.excalidraw.png')) {
      return await parseExcalidrawPng(file);
    } else if (file.name.endsWith('.excalidraw.svg')) {
      return await parseExcalidrawSvg(file);
    } else {
      return await parseExcalidrawJson(file);
    }
  } catch (error) {
    console.error('Error importing file:', error);
    showToast(MESSAGES.IMPORT_FAILED, 'error');
    return null;
  }
}

/**
 * 解析 .excalidraw JSON 檔案
 * @param {File} file 
 * @returns {Promise<Object>}
 */
async function parseExcalidrawJson(file) {
  const text = await file.text();
  const data = JSON.parse(text);

  // 驗證基本結構
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid JSON structure');
  }

  // 確保必要欄位存在
  return {
    elements: data.elements || [],
    appState: data.appState || { viewBackgroundColor: '#ffffff' },
    files: data.files || {},
  };
}

/**
 * 解析 .excalidraw.png 檔案（從 PNG metadata 提取）
 * @param {File} file 
 * @returns {Promise<Object>}
 */
async function parseExcalidrawPng(file) {
  // 使用 Excalidraw 的 loadFromBlob API（如果可用）
  if (typeof window.ExcalidrawLib !== 'undefined' && window.ExcalidrawLib.loadFromBlob) {
    const result = await window.ExcalidrawLib.loadFromBlob(file, null, null, null);
    return {
      elements: result.elements || [],
      appState: result.appState || { viewBackgroundColor: '#ffffff' },
      files: result.files || {},
    };
  }

  // 回退：嘗試從 PNG 的 tEXt chunk 提取
  throw new Error('PNG parsing requires ExcalidrawLib.loadFromBlob');
}

/**
 * 解析 .excalidraw.svg 檔案（從 SVG metadata 提取）
 * @param {File} file 
 * @returns {Promise<Object>}
 */
async function parseExcalidrawSvg(file) {
  // 使用 Excalidraw 的 loadFromBlob API（如果可用）
  if (typeof window.ExcalidrawLib !== 'undefined' && window.ExcalidrawLib.loadFromBlob) {
    const result = await window.ExcalidrawLib.loadFromBlob(file, null, null, null);
    return {
      elements: result.elements || [],
      appState: result.appState || { viewBackgroundColor: '#ffffff' },
      files: result.files || {},
    };
  }

  // 回退：嘗試從 SVG 的 metadata 提取
  const text = await file.text();
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'image/svg+xml');
  
  // 尋找 Excalidraw 內嵌資料
  const metadataElement = doc.querySelector('metadata');
  if (metadataElement) {
    const dataElement = metadataElement.querySelector('excalidraw\\:data, data');
    if (dataElement) {
      const base64Data = dataElement.textContent;
      const jsonString = atob(base64Data);
      return JSON.parse(jsonString);
    }
  }

  throw new Error('SVG does not contain Excalidraw data');
}

/**
 * 驗證檔案類型
 * @param {string} filename 
 * @returns {boolean}
 */
export function isValidFileType(filename) {
  if (!filename) return false;
  return SUPPORTED_IMPORT_EXTENSIONS.some(ext => filename.toLowerCase().endsWith(ext));
}

/**
 * 載入場景到 Excalidraw
 * @param {Object} sceneData 
 * @returns {boolean}
 */
export function loadSceneToExcalidraw(sceneData) {
  const api = getExcalidrawAPI();
  if (!api) {
    console.error('Excalidraw API not available');
    return false;
  }

  try {
    // 更新場景
    api.updateScene({
      elements: sceneData.elements || [],
      appState: sceneData.appState || {},
    });

    // 添加檔案（如果有）
    if (sceneData.files && Object.keys(sceneData.files).length > 0) {
      api.addFiles(Object.values(sceneData.files));
    }

    // 滾動到內容
    setTimeout(() => {
      api.scrollToContent();
    }, 100);

    return true;
  } catch (error) {
    console.error('Error loading scene:', error);
    return false;
  }
}

/**
 * 開啟檔案選擇器並匯入
 * @returns {Promise<Object|null>}
 */
export function openImportDialog() {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = SUPPORTED_IMPORT_EXTENSIONS.join(',');
    
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) {
        resolve(null);
        return;
      }

      const result = await importExcalidrawFile(file);
      resolve(result);
    };

    input.click();
  });
}

/**
 * 從場景資料取得檔名（用於匯入時的標題）
 * @param {File} file 
 * @returns {string}
 */
export function getFilenameAsTitle(file) {
  if (!file || !file.name) return '匯入的白板';
  
  // 移除副檔名
  let name = file.name;
  for (const ext of SUPPORTED_IMPORT_EXTENSIONS) {
    if (name.toLowerCase().endsWith(ext)) {
      name = name.slice(0, -ext.length);
      break;
    }
  }
  
  return name || '匯入的白板';
}
